import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';

/**
 * ProfileMenu — the logged-in identity chip in the a5suite header, mirroring
 * integravizija.lt: a round initials avatar (3px navy ring) that opens a small
 * popover with WHO is logged in (email + role). The PROFILE ITSELF is not
 * rebuilt here — the "Paskyra" entry opens the integravizija.lt dashboard
 * (the account's home: name, companies, licences) in a new tab; only what is
 * inherently a5suite-local stays: Atsijungti. (Mano užklausos moved to the
 * sidebar per finding 44, 2026-09-12 — one entry per capability.)
 */
interface MeResponse {
  username: string;
  role: string;
  displayName?: string | null;
}

/**
 * Two letters for the round avatar — the SAME algorithm as integravizija.lt
 * (src/lib/initials.ts): a name wins (first + last word initials; single word
 * → first two chars), otherwise the email local part's first two chars, so
 * gedas.kns@… shows GE on BOTH sites (Lithuanian-locale uppercase).
 */
function accountInitials(name: string | null | undefined, email: string | null | undefined): string {
  const upperLt = (s: string) => s.toLocaleUpperCase('lt');
  const firstChar = (s: string) => Array.from(s)[0] ?? '';
  const takeTwo = (s: string) => upperLt(Array.from(s).slice(0, 2).join(''));

  const n = (name ?? '').trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return upperLt(firstChar(parts[0]!) + firstChar(parts[parts.length - 1]!));
    return takeTwo(parts[0] ?? '');
  }
  const local = (email ?? '').split('@')[0]?.trim() ?? '';
  if (local) return takeTwo(local);
  return '?';
}

function roleLabel(role: string | undefined, t: (k: string) => string): string {
  switch ((role ?? '').toLowerCase()) {
    case 'admin': return t('profile.role.admin');
    case 'senior_accountant': return t('profile.role.seniorAccountant');
    case 'accountant': return t('profile.role.accountant');
    case 'viewer': return t('profile.role.viewer');
    default: return role ?? '';
  }
}

export default function ProfileMenu({ onLogout }: { onLogout: () => void }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/auth/me'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  // The account lives on the SHOP — open the real profile, not a rebuild.
  const locale = i18n.language?.startsWith('en') ? 'en' : 'lt';
  const accountUrl = `https://integravizija.lt/${locale}/dashboard`;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(prev => !prev)}
        aria-label={t('profile.title')}
        title={me?.username ?? t('profile.title')}
        className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full border-[3px] border-[#071E3B] bg-white text-xs font-semibold tracking-wide text-[#071E3B] hover:bg-gray-50 transition-colors"
      >
        {accountInitials(me?.displayName, me?.username)}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20 p-3">
            <p className="text-sm font-medium text-gray-900 truncate" title={me?.username}>
              {me?.displayName || me?.username || '—'}
            </p>
            <p className="text-xs text-gray-400 truncate">{me?.displayName ? me?.username : roleLabel(me?.role, t)}</p>
            {me?.displayName && <p className="text-xs text-gray-400">{roleLabel(me?.role, t)}</p>}
            <div className="border-t border-gray-100 mt-2 pt-2 space-y-1">
              <a
                href={accountUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
              >
                <span>{t('profile.manage')}</span>
                <span aria-hidden className="text-gray-300">↗</span>
              </a>
              <button
                onClick={() => { setOpen(false); onLogout(); }}
                className="w-full text-left px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md"
              >
                {t('auth.signOut')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
